import React from 'react'
import isEqual from 'lodash/isEqual'
import { Icon, Text } from '@lightspeed/design-system-react'
import { T } from '@transifex/react'

import { PromotionDetails } from '../../../models/promotion'
import { LineItem } from '../../../models/line-item'
import {
  getVariantsDisplayNames,
  hasVariants,
  isPriceDiscounted,
} from '../../../functions/lineItem'
import { useAppSelector } from '../../../state/hooks'
import { selectLineItems } from '../../../state/slices/currentSale/currentSaleSlice'
import { useSpecialOrderProduct } from '../../special-order-product/useSpecialOrderProduct'
import { FulfilmentType } from '../../../models/fulfilment'
import { useFulfillmentTakeNowSaleData } from '../useFulfillmentTakeNowSaleData'
import { LineItemTotalPrice } from './LineItemTotalPrice'
import { LineItemTotalBasePrice } from './LineItemTotalBasePrice'
import { LineItemCustomFields } from './LineItemCustomFields'
import { SpecialOrderProductTag } from './SpecialOrderProductTag'

export const getSaleLineGroupingText = ({
  isContinuedSale,
  fulfillmentType,
}: {
  isContinuedSale: boolean
  fulfillmentType: FulfilmentType
}) => {
  if (fulfillmentType === FulfilmentType.DELIVERY) {
    return isContinuedSale ? 'For delivery' : 'Deliver later'
  }
  if (fulfillmentType === FulfilmentType.PICKUP) {
    return isContinuedSale ? 'For pickup' : 'Pick up later'
  }
  return isContinuedSale ? 'Taken at time of sale' : 'Take now'
}

interface PromotionProps {
  promotionDetails: PromotionDetails[]
}

const LineItemPromotionDetails: React.FC<PromotionProps> = ({
  promotionDetails,
}) => (
  <>
    {promotionDetails.map((promotionDetail, i) => (
      <div
        key={i}
        className="vd-text-supplementary"
        data-cy="sale-summary-promo-data"
      >
        <div>
          <Icon icon="ticket" /> {promotionDetail.name}
        </div>
        {promotionDetail.promoCode && (
          <div className="li-cell-summary-promo-code">
            <Icon icon="key" /> {promotionDetail.promoCode}
          </div>
        )}
      </div>
    ))}
  </>
)

export const SaleSummaryLineItems: React.FC = () => {
  // Ensures we don't error out on the sale confirmation view as the sale manager is being initialised
  const lineItems = useAppSelector(selectLineItems, isEqual) || []
  const {
    takeNowLines,
    pickupLaterLines,
    deliveryLines,
    shouldShowGroupingOfSaleLines,
    isContinuedSale,
  } = useFulfillmentTakeNowSaleData()

  if (!lineItems.length) {
    return null
  }

  const takeNowGroupingText = getSaleLineGroupingText({
    isContinuedSale,
    fulfillmentType: FulfilmentType.UNSPECIFIED,
  })
  const pickupLaterGroupingText = getSaleLineGroupingText({
    isContinuedSale,
    fulfillmentType: FulfilmentType.PICKUP,
  })
  const deliveryGroupingText = getSaleLineGroupingText({
    isContinuedSale,
    fulfillmentType: FulfilmentType.DELIVERY,
  })

  if (shouldShowGroupingOfSaleLines) {
    return (
      <ul className="sale-summary-list">
        {takeNowLines.length > 0 ? (
          <>
            <li className="fulfillment-banner" data-testid="take-now-banner">
              <Text
                key={takeNowGroupingText}
                type="label"
                className="banner-text"
              >
                <T
                  _str={takeNowGroupingText}
                  _context="Fulfillment message banner"
                />
              </Text>
            </li>
            {takeNowLines.map(lineItem => (
              <SaleSummaryLineItem
                lineItem={lineItem as LineItem}
                key={lineItem.localId}
              />
            ))}
          </>
        ) : null}
        {deliveryLines.length > 0 ? (
          <>
            <li className="fulfillment-banner" data-testid="delivery-banner">
              <Text
                key={deliveryGroupingText}
                type="label"
                className="banner-text"
              >
                <T
                  _str={deliveryGroupingText}
                  _context="Fulfillment message banner"
                />
              </Text>
            </li>
            {deliveryLines.map(lineItem => (
              <SaleSummaryLineItem lineItem={lineItem} key={lineItem.localId} />
            ))}
          </>
        ) : null}
        {pickupLaterLines.length > 0 ? (
          <>
            <li className="fulfillment-banner" data-testid="pickup-banner">
              <Text
                key={pickupLaterGroupingText}
                type="label"
                className="banner-text"
              >
                <T
                  _str={pickupLaterGroupingText}
                  _context="Fulfillment message banner"
                />
              </Text>
            </li>
            {pickupLaterLines.map(lineItem => (
              <SaleSummaryLineItem lineItem={lineItem} key={lineItem.localId} />
            ))}
          </>
        ) : null}
      </ul>
    )
  }

  return (
    <ul className="sale-summary-list">
      {lineItems.map(lineItem => (
        <SaleSummaryLineItem lineItem={lineItem} key={lineItem.localId} />
      ))}
    </ul>
  )
}

const SaleSummaryLineItemComponent: React.FC<{ lineItem: LineItem }> = ({
  lineItem,
}) => {
  const variantNames = hasVariants(lineItem)
    ? getVariantsDisplayNames(lineItem)
    : ''
  const isSpecialOrderProduct = useSpecialOrderProduct()
  const showSpecialOrderProductTag = isSpecialOrderProduct(lineItem)
  return (
    <ul
      className="li-cell li-cell--readonly vd-pt6"
      data-line-item-id={lineItem.localId}
      data-adjusted={lineItem.adjusted}
    >
      <li className="li-cell-summary-container li-cell-summary-container--readonly vd-pt0 vd-pb0">
        <div className="li-cell-summary-left-group">
          <div className="li-cell-summary li-cell-summary--quantity">
            {lineItem.quantity}
          </div>
          <div className="li-cell-summary li-cell-summary--name-container">
            <div className="li-cell-summary pro-li-cell-summary-name">
              {lineItem.product.name}
              {variantNames && (
                <div className="vd-text-supplementary">{variantNames}</div>
              )}
              {lineItem.giftCardNumber && (
                <div className="vd-text-supplementary">
                  Card Number: {lineItem.giftCardNumber}
                </div>
              )}

              {lineItem.promotionDetails && (
                <LineItemPromotionDetails
                  promotionDetails={lineItem.promotionDetails}
                />
              )}

              <LineItemCustomFields lineItem={lineItem} />
            </div>
          </div>
        </div>
        <div className="li-cell-summary-right-group">
          <div className="li-cell-summary li-cell-summary--totals">
            <div className="li-cell-summary--total-price">
              <LineItemTotalPrice type="normal" totals={lineItem.totals} />
            </div>
            {isPriceDiscounted(lineItem) && (
              <div
                className="li-cell-summary--total-base-price"
                data-cy="line-item-total-base-price"
              >
                <LineItemTotalBasePrice totals={lineItem.totals} />
              </div>
            )}
          </div>
        </div>
      </li>
      {lineItem.note && (
        <li className="li-cell-summary-content vd-text-supplementary vd-pt2">
          <div className="wr-honour-line-breaks">Note: {lineItem.note}</div>
        </li>
      )}
      {showSpecialOrderProductTag && (
        <li className="vd-pt2">
          <SpecialOrderProductTag className="li-cell-summary-content" />
        </li>
      )}
    </ul>
  )
}

const SaleSummaryLineItem = React.memo(SaleSummaryLineItemComponent, isEqual)
